import { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';

import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';

import URLS from 'URLS';

const IframeHighlightContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'highlighted',
})<{ highlighted: boolean }>(({ theme, highlighted }) => ({
  position: 'absolute',
  top: 0,
  left: '50%',
  transform: 'translateX(-50%)',
  right: 'auto',
  bottom: 0,
  pointerEvents: 'none',
  maxWidth: '800px',
  width: '100%',
  [theme.breakpoints.down('xl')]: {
    maxWidth: '400px',
  },
  transitionProperty: 'border-width',
  transitionDuration: '200ms',
  border: 'solid',
  borderRadius: '10px',
  borderColor: theme.palette.primary.main,
  borderWidth: highlighted ? '7px' : '0px',
}));

const formatCalendlyUrl = ({
  url,
  embedType = 'Inline',
  backgroundColor,
  textColor,
  primaryColor,
  hideGdprBanner = true,
  hideEventTypeDetails = true,
  hideLandingPageDetails = true,
}: {
  url: string;
  embedType?: 'Inline' | 'PopupWidget' | 'PopupButton';
  backgroundColor?: string;
  textColor?: string;
  primaryColor?: string;
  hideGdprBanner?: boolean;
  hideEventTypeDetails?: boolean;
  hideLandingPageDetails?: boolean;
}) => {
  const queryParams = [
    `embed_type=${embedType}`,
    backgroundColor ? `background_color=${backgroundColor}` : null,
    textColor ? `text_color=${textColor}` : null,
    primaryColor ? `primary_color=${primaryColor}` : null,
    hideGdprBanner ? `hide_gdpr_banner=1` : null,
    hideEventTypeDetails ? `hide_event_type_details=1` : null,
    hideLandingPageDetails ? `hide_landing_page_details=1` : null,
    `embed_domain=1`, // Required for iframe communication
  ]
    .filter(Boolean)
    .join('&');

  return `${url}?${queryParams}`;
};

interface CalendlyBookMeetingProps {
  url?: string;
}

export interface CalendlyBookMeetingRef {
  focusCalendar: () => void;
}

const normalizeCssHeight = (height: unknown): string | null => {
  if (typeof height === 'number' && Number.isFinite(height) && height > 0) {
    return `${height}px`;
  }

  if (typeof height === 'string') {
    const trimmedHeight = height.trim();

    if (!trimmedHeight) {
      return null;
    }

    if (/^\d+(\.\d+)?$/.test(trimmedHeight)) {
      return `${trimmedHeight}px`;
    }

    if (/^\d+(\.\d+)?(px|vh|vw|%)$/i.test(trimmedHeight)) {
      return trimmedHeight;
    }
  }

  return null;
};

const CalendlyBookMeeting = forwardRef<CalendlyBookMeetingRef, CalendlyBookMeetingProps>(({ url }: CalendlyBookMeetingProps, ref) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const highlightTimeoutIdsRef = useRef<number[]>([]);
  const [isHighlighted, setIsHighlighted] = useState<boolean>(false);
  const formattedUrl = formatCalendlyUrl({
    url: url ? url : URLS.CALENDLY,
  });

  const clearHighlightTimeouts = () => {
    highlightTimeoutIdsRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    highlightTimeoutIdsRef.current = [];
  };

  const focusCalendar = () => {
    if (iframeRef.current) {
      iframeRef.current.scrollIntoView({ behavior: 'smooth' });
      activateHighlighting();
    }
  };

  const activateHighlighting = (numberOfFlashes = 1, period = 1000, startDelay = 600, offPercentage = 50) => {
    const offTime = (period * offPercentage) / 100;
    for (let i = 0; i < numberOfFlashes; i++) {
      const highlightOnTimeoutId = window.setTimeout(
        () => {
          setIsHighlighted(true);
        },
        startDelay + i * period,
      );
      highlightTimeoutIdsRef.current.push(highlightOnTimeoutId);

      const highlightOffTimeoutId = window.setTimeout(
        () => {
          setIsHighlighted(false);
        },
        startDelay + (i + 1) * period - offTime,
      );
      highlightTimeoutIdsRef.current.push(highlightOffTimeoutId);
    }
  };

  useEffect(() => {
    return () => {
      clearHighlightTimeouts();
    };
  }, []);

  useImperativeHandle(ref, () => ({
    focusCalendar,
  }));

  useEffect(() => {
    const iframeOrigin = (() => {
      try {
        return new URL(formattedUrl).origin;
      } catch {
        return null;
      }
    })();

    const handlePageHeightMessage = (event: MessageEvent) => {
      if (!iframeRef.current || event.data?.event !== 'calendly.page_height') {
        return;
      }

      if (iframeOrigin && event.origin !== iframeOrigin) {
        return;
      }

      if (event.source !== iframeRef.current.contentWindow) {
        return;
      }

      const normalizedHeight = normalizeCssHeight(event.data?.payload?.height);
      if (!normalizedHeight) {
        return;
      }

      iframeRef.current.style.height = normalizedHeight;
    };

    window.addEventListener('message', handlePageHeightMessage);

    return () => {
      window.removeEventListener('message', handlePageHeightMessage);
    };
  }, [formattedUrl]);

  return (
    <Box
      sx={(theme) => ({
        position: 'relative',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        width: '100%',
        [theme.breakpoints.down('xl')]: {
          mt: 2,
          mb: 2,
        },
      })}>
      <IframeHighlightContainer highlighted={isHighlighted} />
      <Box
        component='iframe'
        ref={iframeRef}
        src={formattedUrl}
        sx={(theme) => ({
          width: '100%',
          minHeight: '600px',
          border: 'none',
          minWidth: '320px',
          maxWidth: '1000px',
          [theme.breakpoints.down('xl')]: {
            maxWidth: '400px',
          },
        })}
        title='Calendly Booking'
      />
    </Box>
  );
});

CalendlyBookMeeting.displayName = 'CalendlyBookMeeting';

export default CalendlyBookMeeting;
